import { XhprofRawData } from '../xhprof/XhprofReportGenerator'
import { Bottleneck } from './Bottleneck'
import { DateTime } from './DateTime'
import { Operation } from './Operation'
import moment from 'moment/moment'

interface LayerReportStats {
    ct: number
    wt: number
    perc?: number
    max?: number
}

export interface LayerReport {
    db?: LayerReportStats
    http?: LayerReportStats
    es?: LayerReportStats
    cache?: LayerReportStats
    redis?: LayerReportStats
    mongo?: LayerReportStats
    fio?: LayerReportStats
    uio?: LayerReportStats
    io?: LayerReportStats
    al?: LayerReportStats
    runq?: LayerReportStats
    session?: LayerReportStats
    apcu?: LayerReportStats
    shell?: LayerReportStats
    email?: LayerReportStats
    sleep?: LayerReportStats
    dns?: LayerReportStats
    queue?: LayerReportStats
    gc?: LayerReportStats
    ct?: LayerReportStats
}

export interface TraceSummary {
    id: string
    name: string | null
    parseType: number
    operation: string
    operationIdentifier?: string
    operationId?: number
    server: string
    environment: string
    service: string
    callCount: number
    measureDate: DateTime
    wallTime: number
    memory: number
    statusCode: number | null
    method: string
    url: string
    bottlenecks: Bottleneck
    bottleneckTypes: string[]
    layerReport: LayerReport
    title: string
    errorType: string | null
    isIgnoredErrorType: boolean
    traceId?: string
    parentSpanId: string | null
    spanIds: string[]
    transactionTags: string[]
}

export interface TraceSummarySingleView {
    id: string
    name: string | null
    index: string
    applicationId: number
    correlationId: string | null
    server: { hostname: string; name: string; id: number }
    operation: Operation // todo: check
    measureDate: DateTime
    wallTime: number
    memory: number | null
    title: string | null
    statusCode: number | null
    method: string | null
    url: string | null
    layers: LayerReport
    bottlenecks: number
    traceId: string | null
    parentSpanId: string | null
    spans: any // @todo: type properly
    spanIds: string[]
    transactionTags: string[]
    parseType: number // @todo enum
    releaseEventId: number | null
    retentionDays: number
    environment: string
    service: string
    errorType: string | null
    isIgnoredErrorType: boolean
    data: null // @todo
    rawData: XhprofRawData // @todo
    callCount: number | null
    sharedTraceInfo: {
        sharedTraceId: string
        sharedDate: string
        sharedUntil: string | null
        sharedByUserId: string
    } | null
    hasActiveShare: boolean
}

export interface TraceList {
    profiles: TraceSummary[]
    total: number
    aggregations: { [name: string]: number }
}

export const isCallgraph = (trace: TraceSummary | TraceSummarySingleView): boolean => (trace.parseType & 1) > 0
export const isAggregatedTrace = (trace: TraceSummary | TraceSummarySingleView): boolean => (trace.parseType & 4) > 0

const traceDate = (trace: TraceSummarySingleView): moment.Moment => {
    const format = 'YYYY-MM-DD HH:mm:ss Z'

    if (trace.spans[0]?.annotations?.trace_aggregation_report?.min_date) {
        return moment.utc(trace.spans[0]?.annotations?.trace_aggregation_report?.min_date, format)
    }

    return moment.utc(trace.measureDate.date, format)
}

export const traceIsOlder = (older: TraceSummarySingleView, newer: TraceSummarySingleView): boolean => {
    const olderDate = traceDate(older).unix()
    const newerDate = traceDate(newer).unix()

    // we can have two traces from the same minute
    // in that case, just return natural sort of the trace IDs to keep
    // this deterministic
    if (olderDate === newerDate) {
        return older.id.localeCompare(newer.id) < 0
    }

    return olderDate < newerDate
}

export type TraceRelatedInTimeLabel = 'After' | 'Before'

export interface TraceDiffTranslation {
    from: TraceRelatedInTimeLabel
    to: TraceRelatedInTimeLabel
}

export function renderTraceDiffTranslationEarlierLater(item: TraceRelatedInTimeLabel): string {
    return item === 'After' ? 'later' : 'earlier'
}
